/**
 * Список показателей датасета и их источники в Our World in Data.
 *
 * `owidSlug` — хвост URL вида https://ourworldindata.org/grapher/<slug>;
 * `column` — имя столбца в скачанном CSV (`useColumnShortNames=true`);
 * slug'и и столбцы сверены с реальными выгрузками OWID (октябрь 2026).
 * Slug может нести параметры графика после `?` (см. `tertiary`).
 * `fallback` — чем заполнять пропуск: медианой региона ВОЗ или константой.
 */
export const INDICATORS = [
  'births',
  'population',
  'lifeExpF',
  'lifeExpM',
  'u5mr',
  'imr',
  'gdpPpp',
  'gini',
  'schoolYears',
  'tertiary',
  'agriShare',
  'fertility',
  'maternalMortality',
  'hiv',
  'homicide',
  'smoking',
  'oopHealth',
  'childMarriage',
  'lifeSatisfaction',
  'urban',
  'remittanceCost',
] as const;

export type Indicator = (typeof INDICATORS)[number];

export interface IndicatorSource {
  indicator: Indicator;
  owidSlug: string;
  column: string;
  unit: string;
  fallback: 'region' | 'constant';
  constant?: number;
}

export const SOURCES: IndicatorSource[] = [
  { indicator: 'births', owidSlug: 'number-of-births-per-year', column: 'births__sex_all__age_all__variant_estimates', unit: 'persons', fallback: 'region' },
  { indicator: 'population', owidSlug: 'population', column: 'population_historical', unit: 'persons', fallback: 'region' },
  { indicator: 'lifeExpF', owidSlug: 'life-expectancy-of-women-vs-life-expectancy-of-men', column: 'life_expectancy__sex_female__age_0__variant_estimates', unit: 'years', fallback: 'region' },
  { indicator: 'lifeExpM', owidSlug: 'life-expectancy-of-women-vs-life-expectancy-of-men', column: 'life_expectancy__sex_male__age_0__variant_estimates', unit: 'years', fallback: 'region' },
  { indicator: 'u5mr', owidSlug: 'child-mortality', column: 'child_mortality_rate', unit: 'per 100 live births', fallback: 'region' },
  { indicator: 'imr', owidSlug: 'infant-mortality', column: 'observation_value__indicator_infant_mortality_rate__sex_total__wealth_quintile_total__unit_of_measure_deaths_per_100_live_births', unit: 'per 100 live births', fallback: 'region' },
  { indicator: 'gdpPpp', owidSlug: 'gdp-per-capita-worldbank', column: 'ny_gdp_pcap_pp_kd', unit: 'intl $ PPP', fallback: 'region' },
  { indicator: 'gini', owidSlug: 'gini-coefficient-wb', column: 'gini__welfare_type_income_or_consumption__table_income_or_consumption_consolidated__survey_comparability_no_spells', unit: 'index 0-1', fallback: 'region' },
  { indicator: 'schoolYears', owidSlug: 'mean-years-of-schooling-long-run', column: 'mf_youth_and_adults__15_64_years__average_years_of_education', unit: 'years', fallback: 'region' },
  { indicator: 'tertiary', owidSlug: 'school-enrolment?enrolment_type=gross_enrolment&level=tertiary&sex=both', column: 'gross_enrolment_ratio_for_tertiary_education__both_sexes__pct__ger_5t8', unit: '%', fallback: 'region' },
  { indicator: 'agriShare', owidSlug: 'share-of-employment-in-agriculture', column: 'share_employed_agriculture', unit: '%', fallback: 'region' },
  { indicator: 'fertility', owidSlug: 'children-per-woman-un', column: 'fertility_rate__sex_all__age_all__variant_estimates', unit: 'children per woman', fallback: 'region' },
  { indicator: 'maternalMortality', owidSlug: 'maternal-mortality', column: 'mmr', unit: 'per 100 000 live births', fallback: 'region' },
  { indicator: 'hiv', owidSlug: 'share-of-the-population-infected-with-hiv', column: 'hiv_prevalence__age_15_49__sex_total__estimate_central', unit: '% of 15-49', fallback: 'region' },
  { indicator: 'homicide', owidSlug: 'homicide-rate-unodc', column: 'value__category_total__sex_total__age_total__unit_of_measurement_rate_per_100_000_population', unit: 'per 100 000', fallback: 'region' },
  { indicator: 'smoking', owidSlug: 'share-of-adults-who-smoke', column: 'tobacco_use_pct_age_std__sex_both_sexes', unit: '% of adults', fallback: 'region' },
  { indicator: 'oopHealth', owidSlug: 'share-of-out-of-pocket-expenditure-on-healthcare', column: 'sh_xpd_oopc_ch_zs', unit: '% of health spending', fallback: 'region' },
  { indicator: 'childMarriage', owidSlug: 'women-married-by-age-18', column: 'sp_m18_2024_fe_zs', unit: '% of women 20-24', fallback: 'region' },
  { indicator: 'lifeSatisfaction', owidSlug: 'happiness-cantril-ladder', column: 'cantril_ladder_score', unit: 'score 0-10', fallback: 'region' },
  { indicator: 'urban', owidSlug: 'share-of-population-urban', column: 'share__area_type_urban__data_type_estimates', unit: '%', fallback: 'region' },
  { indicator: 'remittanceCost', owidSlug: '', column: '', unit: '% of amount', fallback: 'constant', constant: 6.0 },
];
